import { useEffect, useState, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useCare } from '../contexts/CareContext'
import LoadingSpinner from '../components/shared/LoadingSpinner'
import EmptyState from '../components/shared/EmptyState'
import CareLogForm from '../components/care-log/CareLogForm'
import CareLogCard from '../components/care-log/CareLogCard'

export default function CareLogPage() {
  const { recipient } = useCare()
  const navigate = useNavigate()
  const [mode, setMode] = useState('list') // 'list' | 'form'
  const [logs, setLogs] = useState([])
  const [loading, setLoading] = useState(true)
  const [from, setFrom] = useState('')
  const [to, setTo] = useState('')
  const [showFilter, setShowFilter] = useState(false)
  const [live, setLive] = useState(false)
  const fromRef = useRef(from)
  const toRef = useRef(to)
  fromRef.current = from
  toRef.current = to

  const load = useCallback(async () => {
    if (!recipient) return
    let q = supabase
      .from('care_logs')
      .select('*, profile:profiles(full_name)')
      .eq('care_recipient_id', recipient.id)
      .order('log_date', { ascending: false })
      .order('created_at', { ascending: false })
    if (fromRef.current) q = q.gte('log_date', fromRef.current)
    if (toRef.current) q = q.lte('log_date', toRef.current)
    const { data } = await q
    setLogs(data || [])
    setLoading(false)
  }, [recipient])

  useEffect(() => {
    load()
  }, [load, from, to])

  // Realtime: refetch when any family member changes a care log for this recipient.
  useEffect(() => {
    if (!recipient) return
    const channel = supabase
      .channel(`care-logs-${recipient.id}`)
      .on(
        'postgres_changes',
        { event: '*', schema: 'public', table: 'care_logs', filter: `care_recipient_id=eq.${recipient.id}` },
        () => load()
      )
      .subscribe((status) => {
        if (status === 'SUBSCRIBED') setLive(true)
      })
    return () => {
      supabase.removeChannel(channel)
      setLive(false)
    }
  }, [recipient, load])

  if (!recipient) return <LoadingSpinner />

  if (mode === 'form') {
    return (
      <CareLogForm
        recipientId={recipient.id}
        onCancel={() => setMode('list')}
        onDone={() => { setMode('list'); load() }}
      />
    )
  }

  return (
    <div className="stack">
      <button onClick={() => navigate('/')} style={{ background: 'none', border: 'none', color: 'var(--teal)', fontWeight: 600, alignSelf: 'flex-start', padding: 0 }}>
        ← Home
      </button>

      <div className="spread">
        <div>
          <h1 className="title">Care log</h1>
          <p className="subtitle mt-4">
            {live && <span className="badge badge-green" style={{ marginRight: 6 }}>● Live</span>}
            History for {recipient.preferred_name || recipient.full_name}
          </p>
        </div>
        <button className="btn" onClick={() => setMode('form')}>+ Add</button>
      </div>

      {/* Filter */}
      <div className="card stack" style={{ gap: 10 }}>
        <button onClick={() => setShowFilter((s) => !s)} className="spread" style={{ background: 'none', border: 'none', padding: 0, width: '100%' }}>
          <span className="section-title">Filter by date</span>
          <span className="muted">{showFilter ? '▲' : '▼'}</span>
        </button>
        {showFilter && (
          <>
            <div className="row" style={{ gap: 10 }}>
              <div className="field grow"><label className="label">From</label><input type="date" className="input" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
              <div className="field grow"><label className="label">To</label><input type="date" className="input" value={to} onChange={(e) => setTo(e.target.value)} /></div>
            </div>
            {(from || to) && <button className="btn btn-sm btn-ghost" onClick={() => { setFrom(''); setTo('') }}>Clear filter</button>}
          </>
        )}
      </div>

      {loading ? (
        <LoadingSpinner label="Loading logs…" />
      ) : logs.length === 0 ? (
        <EmptyState
          icon="📝"
          title={from || to ? 'No logs in this range' : 'No care logs yet'}
          message={from || to ? 'Try a wider date range.' : 'Add the first shift log — it takes under 3 minutes.'}
          action={!from && !to && <button className="btn" onClick={() => setMode('form')}>+ Add care log</button>}
        />
      ) : (
        <div className="stack" style={{ gap: 10 }}>
          {logs.map((log) => <CareLogCard key={log.id} log={log} />)}
        </div>
      )}

      <div style={{ height: 8 }} />
    </div>
  )
}
